/**
 * Protected Route Component - F1 Unified Auth
 *
 * Wraps routes that require authentication using the unified AuthContext.
 * Automatically redirects unauthenticated users to the sign-in page.
 *
 * Usage:
 * ```tsx
 * <Route path="/admin" element={
 *   <ProtectedRoute requiredRole="admin">
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 * ```
 */

import { ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Navigate, useLocation } from 'react-router-dom'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface ProtectedRouteProps {
  children: ReactNode
  /** Optional: Specify required role for access */
  requiredRole?: 'admin' | 'user'
  /** Optional: Custom redirect path (defaults to /handler/sign-in) */
  redirectTo?: string
}

export function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = '/handler/sign-in',
}: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, hasRole } = useAuth()
  const location = useLocation()

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--color-background)',
      }}>
        <LoadingSpinner
          size="lg"
          label="Checking authentication"
          showLabel
          centered
        />
      </div>
    )
  }

  // Redirect to sign-in if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Check role-based access control if required
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/unauthorized" replace />
  }

  // User is authenticated - render children
  return <>{children}</>
}
